import React from "react";

import { cn } from "../../../../lib/utils";
import { Checkbox } from "../../../ui/checkbox";
import { Label } from "../../../ui/label";

/**
 * BdcConsents — consentimientos front-only del paso nativo de Buró de Crédito.
 *
 * Presentacional/controlado: el orquestador (F3) es dueño del estado de ambos
 * consentimientos y los recibe vía callbacks. Estos valores NO viajan en el
 * payload del NIP; sólo gatean el botón "Continuar". El backend los persiste
 * server-side dentro del `bdc/start`.
 *
 * Textos legales EXACTOS (blueprint Addendum 2 A):
 *  - `accept_tyc`: T&C + aviso de privacidad (links multiburo.com.mx/terminos y /privacidad).
 *  - `accept_electronic_media`: consentimiento de medios electrónicos / NIP.
 *  - Disclaimer LRSIC art. 28 como texto informativo (no checkbox, no se envía).
 */

const DISCLAIMER_COMPANY = "Multiburó SAPI de C.V.";

export interface BdcConsentsProps {
  /** Estado del consentimiento de T&C + aviso de privacidad. */
  acceptTyc: boolean;
  /** Estado del consentimiento de medios electrónicos / NIP. */
  acceptElectronicMedia: boolean;
  /** Callback al cambiar `accept_tyc`. */
  onAcceptTycChange: (value: boolean) => void;
  /** Callback al cambiar `accept_electronic_media`. */
  onAcceptElectronicMediaChange: (value: boolean) => void;
  /** Deshabilita ambos checkboxes (p.ej. durante una mutación). */
  disabled?: boolean;
}

export const BdcConsents: React.FC<BdcConsentsProps> = ({
  acceptTyc,
  acceptElectronicMedia,
  onAcceptTycChange,
  onAcceptElectronicMediaChange,
  disabled = false,
}) => {
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h4 className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Consentimientos requeridos
        </h4>

        {/* Checkbox 1: términos y condiciones + aviso de privacidad */}
        <div
          className={cn(
            "flex items-start gap-3 rounded-xl border p-4 transition-colors",
            acceptTyc
              ? "border-[#28397a]/40 bg-blue-50/50"
              : "border-slate-200 bg-white hover:border-slate-300",
            disabled && "opacity-60",
          )}
        >
          <Checkbox
            id="bdc_accept_tyc"
            checked={acceptTyc}
            onCheckedChange={(checked) => onAcceptTycChange(checked === true)}
            disabled={disabled}
            className="mt-0.5"
          />
          <Label
            htmlFor="bdc_accept_tyc"
            className="cursor-pointer text-sm font-normal leading-relaxed text-slate-600"
          >
            Acepto los{" "}
            <a
              href="https://www.multiburo.com.mx/terminos"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#28397a] hover:underline"
            >
              términos y condiciones
            </a>{" "}
            asi como el{" "}
            <a
              href="https://www.multiburo.com.mx/privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#28397a] hover:underline"
            >
              aviso de privacidad
            </a>
            .
          </Label>
        </div>

        {/* Checkbox 2: medios electrónicos / NIP (texto exacto) */}
        <div
          className={cn(
            "flex items-start gap-3 rounded-xl border p-4 transition-colors",
            acceptElectronicMedia
              ? "border-[#28397a]/40 bg-blue-50/50"
              : "border-slate-200 bg-white hover:border-slate-300",
            disabled && "opacity-60",
          )}
        >
          <Checkbox
            id="bdc_accept_electronic_media"
            checked={acceptElectronicMedia}
            onCheckedChange={(checked) =>
              onAcceptElectronicMediaChange(checked === true)
            }
            disabled={disabled}
            className="mt-0.5"
          />
          <Label
            htmlFor="bdc_accept_electronic_media"
            className="cursor-pointer text-sm font-normal leading-relaxed text-slate-600"
          >
            Otorgo mi consentimiento expreso para acceder a los servicios
            ofrecidos por Multiburó SAPI de C.V mediante medios electrónicos
            tales como NIP.
          </Label>
        </div>
      </div>

      {/* Disclaimer LRSIC art. 28 — texto informativo (no checkbox, no se envía) */}
      <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
        <p className="text-xs leading-relaxed text-slate-500">
          Por este conducto autorizo expresamente a{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span>, para que
          por conducto de sus funcionarios facultados lleve a cabo
          Investigaciones, sobre mi comportamiento Crediticio en las Sociedades
          de Información Crediticia que estime conveniente. Así mismo, declaro
          que conozco la naturaleza y alcance de la información que se
          solicitará, del uso que{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span> hará de tal
          información y de que ésta podrá realizar consultas periódicas de mi
          historial crediticio, consintiendo que esta autorización se encuentre
          vigente por un periodo de 3 años contados a partir de la fecha de su
          expedición y en todo caso durante el tiempo que mantengamos relación
          jurídica. Acepto que este documento quede bajo propiedad de{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span> y/o en las
          sociedades de información crediticia consultadas para efectos de
          control y cumplimiento del artículo 28 de la LRSIC.
        </p>
      </div>
    </div>
  );
};

export default BdcConsents;
